// Pre-load rules decide which plans are visible before the catalog is rendered,
// and which UI controls should be locked based on the account's existing state.
// Rules are based on static product properties, user feature flags, and account
// subscription data.
//
// Call applyProductRulesBeforeLoading(rawCatalog, userFeatures) on the raw API
// response before dispatching to Redux. Filters centers and apps so all
// downstream consumers see only allowed plans.
//
// Call getLockedBillingByGroup(accountItems, groups) once account data is
// available to determine which groups must have their billing toggle locked.

import {
  KEAP_FAMILY_LEGACY_CODES,
  KEAP_FAMILY_LEGACY_ANNUAL_CODES,
} from '../../../utils/constants'

// Returns a map of { [groupId]: boolean } for groups whose billing frequency
// must be locked to match an existing legacy subscription. true = annual,
// false = monthly. Groups not present are unlocked.
export function getLockedBillingByGroup(accountItems = [], groups = []) {
  const locked = {}

  const hasLegacyAnnual = accountItems.some(item =>
    KEAP_FAMILY_LEGACY_ANNUAL_CODES.includes(item.productCode),
  )
  const hasLegacyMonthly = accountItems.some(item =>
    KEAP_FAMILY_LEGACY_CODES.includes(item.productCode),
  )
  if (hasLegacyAnnual || hasLegacyMonthly) {
    const lockedIsAnnual = hasLegacyAnnual
    groups
      .filter(group => group.plans?.some(p => p.code.startsWith('keap_')))
      .forEach(group => {
        locked[group.id] = lockedIsAnnual
      })
  }

  return locked
}

function planPassesLoadRules(plan, userFeatures) {
  // Rule: requiresFlags
  // At least one listed flag must be enabled for the plan to be visible.
  if (plan.requiresFlags && plan.requiresFlags.length > 0) {
    if (!plan.requiresFlags.some(flag => userFeatures[flag])) return false
  }

  // Rule: restrictFlags
  // If any listed flag is enabled, the plan is hidden.
  if (plan.restrictFlags) {
    const anyFlagEnabled = plan.restrictFlags.some(flag => userFeatures[flag])
    if (anyFlagEnabled) return false
  }

  return true
}

export function applyProductRulesBeforeLoading(rawCatalog, userFeatures = {}) {
  if (!rawCatalog) return rawCatalog

  const centers = rawCatalog.centers || []
  const apps = rawCatalog.apps || []

  const filteredCenters = centers.filter(p =>
    planPassesLoadRules(p, userFeatures),
  )
  const filteredApps = apps.filter(p => planPassesLoadRules(p, userFeatures))

  return {
    ...rawCatalog,
    centers: filteredCenters,
    apps: filteredApps,
  }
}
